/**
 * DelegateLanding - Landing page for delegated connection requests
 * v2.26 - 2026-01-30 - Initial implementation
 *
 * When an accountant/bookkeeper clicks the delegation link in their email,
 * they land here. This page:
 * 1. Validates the delegation token
 * 2. Shows info about what they're connecting
 * 3. Starts the OAuth flow when they click "Connect"
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import LinkIcon from '@mui/icons-material/Link';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { authService } from '../services/api';

const DelegateLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [status, setStatus] = useState<{
    valid: boolean;
    connectorType?: string;
    connectorDisplayName?: string;
    tenantName?: string;
    requesterEmail?: string;
    expiresAt?: string;
    error?: string;
  } | null>(null);

  // Check for error or success in URL params (from OAuth callback)
  const urlError = searchParams.get('error');
  const urlSuccess = searchParams.get('success');

  useEffect(() => {
    if (token) {
      checkStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const checkStatus = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const result = await authService.getDelegationStatus(token);
      setStatus(result);
    } catch (err: any) {
      setStatus({
        valid: false,
        error: err.userMessage || 'Failed to validate link'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    if (!token) return;
    setConnecting(true);
    // Redirect to delegation OAuth start endpoint
    window.location.href = `/api/delegation/${token}/start`;
  };

  // Error messages mapping
  const getErrorMessage = (error: string) => {
    switch (error) {
      case 'not_found':
        return 'This connection link was not found or has expired.';
      case 'expired':
        return 'This connection link has expired. Please ask the account owner to send a new link.';
      case 'already_completed':
        return 'This connection has already been completed.';
      case 'oauth_failed':
        return 'Failed to start the authorization. Please try again.';
      default:
        return error || 'An error occurred. Please try again.';
    }
  };

  // Success state
  if (urlSuccess) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
              Successfully Connected!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The data source has been connected. The account owner will now be able to see their data in MyLojic.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You can close this window.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Error state from URL
  if (urlError) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
              Connection Failed
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {getErrorMessage(urlError)}
            </Alert>
            <Button
              variant="contained"
              onClick={() => window.location.href = `/delegate/${token}`}
              sx={{ bgcolor: 'primary.main' }}
            >
              Try Again
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  // Invalid or expired link
  if (!status?.valid) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
              Invalid Link
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {status?.error || 'This connection link is invalid or has expired.'}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Please contact the person who sent you this link to request a new one.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Valid delegation - show connection prompt
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#003366', py: 2, px: 3 }}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
          MyLojic
        </Typography>
      </Box>

      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LinkIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              Connection Request
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You've been asked to connect a data source
            </Typography>
          </Box>

          <Card variant="outlined" sx={{ mb: 4, bgcolor: '#f9f9f9' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountBalanceIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {status.connectorDisplayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    for {status.tenantName}
                  </Typography>
                </Box>
              </Box>

              {status.requesterEmail && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Requested by: {status.requesterEmail}
                </Typography>
              )}
            </CardContent>
          </Card>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Clicking "Connect" will take you to {status.connectorDisplayName} to authorize access.
              You'll need to log in with credentials that have access to the account.
            </Typography>
          </Alert>

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleConnect}
            disabled={connecting}
            sx={{
              bgcolor: '#008080',
              py: 1.5,
              fontSize: '1.1rem',
              '&:hover': { bgcolor: '#006666' }
            }}
          >
            {connecting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              `Connect ${status.connectorDisplayName}`
            )}
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
            This link expires on {new Date(status.expiresAt!).toLocaleDateString()}
          </Typography>
        </Paper>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Powered by <strong>MyLojic</strong> - AI-native analytics for SMBs
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default DelegateLanding;
